import { UserInfo } from "app/containers/Global/types";
import { AppDispatch } from "utils/redux/store";
import {
  ChatbotConversation,
  ChatbotMessages,
  ChatbotResponse,
  IntroMessagesAndPreselectedPrompts,
  TranslatedIntroMessagesAndPreselectedPrompts,
} from "./types";
import {
  appendStreamingResponse,
  getIsThreadIdWeeklyCheckin,
  setChatbotFullScreen,
  setChatbotMessages,
  setCoachBotPrompt,
  setCoachBotPromptReducer,
  setIsCoachbotOpen,
  setIsStreaming,
  setStreamingResponse,
  setThreadId,
} from "./slice";
import { ChatNotification } from "app/components/Notifications/types";
import { dismissNotification } from "app/components/Notifications/slice";
import { StreamingCallbacks } from "utils/aiServiceRequest";
import { captureException } from "utils/sentry";

export const getChatWithDirectReportIntroMessage = (
  usersById: { [id: number]: UserInfo },
  userAccountId: number | string | null,
  subjectUserAccountId: number | string | null
) => {
  const user = usersById[Number(userAccountId)];
  const userName = `${user.firstName} ${user.lastName}`;

  const subjectUser = usersById[Number(subjectUserAccountId)];

  const subjectUserName = subjectUser
    ? `${subjectUser.firstName} ${subjectUser.lastName}`
    : "your direct report?";

  return `Hi ${userName}. How can I help you engage with ${subjectUserName}?`;
};

const letsChatIntroMessages = [
  (managerName: string, subjectName: string) =>
    `Hi ${managerName}. Let's dig into ${subjectName}'s check-in — what would be most helpful to focus on?`,
  (managerName: string, subjectName: string) =>
    `Hi ${managerName}. I'm ready to help you prep for your 1:1 with ${subjectName}. Where should we start?`,
  (managerName: string, subjectName: string) =>
    `Hi ${managerName}, let's talk through ${subjectName}'s check-in together. What's on your mind?`,
  (managerName: string, subjectName: string) =>
    `Hey ${managerName}, let's take a second to chat about ${subjectName}?`,
];

export const getLetsChatIntroMessage = (
  managerName: string,
  subjectName: string
) => {
  const buildMessage =
    letsChatIntroMessages[
      Math.floor(Math.random() * letsChatIntroMessages.length)
    ];
  return buildMessage(managerName, subjectName);
};

export const setSavedConversation = (
  dispatch: AppDispatch,
  conversation: ChatbotConversation
) => {
  dispatch(getIsThreadIdWeeklyCheckin(conversation.threadId));
  dispatch(setIsCoachbotOpen(true));
  dispatch(setThreadId(conversation.threadId));
  dispatch(setChatbotMessages(conversation.messages));
  dispatch(
    setCoachBotPromptReducer({
      chatType: "savedChat",
      userPrompts: [],
    })
  );
  dispatch(setChatbotFullScreen(true));
};

export const onDismissWeeklyCheckInTodo = (
  dispatch: AppDispatch,
  notifications: ChatNotification[],
  threadId: string
) => {
  const foundNotification = notifications.find(
    (notification) => notification.threadId === threadId
  );

  if (foundNotification) {
    dispatch(dismissNotification(foundNotification.notificationId));
  }
};

const updateMessageWithNames = (
  message?: string,
  currentUser?: UserInfo | null,
  subjectUser?: UserInfo | null
) => {
  return (
    message
      ?.replace("$name", currentUser?.firstName ?? "User")
      ?.replace("$subjectName", subjectUser?.firstName ?? "this user") ?? ""
  );
};

export const getIntroMessageAndPreselectedPrompts = (
  translatedIntroMessagesAndPreselectedPrompts: TranslatedIntroMessagesAndPreselectedPrompts,
  type: string,
  currentUser: UserInfo | null,
  subjectUser?: UserInfo
): {
  fullBotIntro: string;
  userPrompts: string[];
} => {
  const translatedIntroMessagesAndPreselectedPrompt: IntroMessagesAndPreselectedPrompts | null =
    translatedIntroMessagesAndPreselectedPrompts[type];

  const fullBotIntro = updateMessageWithNames(
    translatedIntroMessagesAndPreselectedPrompt?.introMessage,
    currentUser,
    subjectUser
  );

  const userPrompts =
    translatedIntroMessagesAndPreselectedPrompt?.preSelectedPrompts.map(
      (prompt) => updateMessageWithNames(prompt, currentUser, subjectUser)
    ) ?? [];

  return {
    fullBotIntro,
    userPrompts,
  };
};

export const onFirstCoachBoChatClick = (
  dispatch: AppDispatch,
  translatedIntroMessagesAndPreselectedPrompts: TranslatedIntroMessagesAndPreselectedPrompts,
  userInfo: UserInfo
) => {
  const { fullBotIntro, userPrompts } = getIntroMessageAndPreselectedPrompts(
    translatedIntroMessagesAndPreselectedPrompts,
    "WelcomeChat",
    userInfo
  );
  dispatch(
    setCoachBotPrompt({
      chatType: "welcomeChat",
      userPrompts,
      fullBotIntro,
    })
  );
};

type StreamingFn = (
  id: string,
  messages: ChatbotMessages,
  callbacks: StreamingCallbacks
) => Promise<{ messages: ChatbotMessages; threadId: string }>;

/**
 * Shared helper for dispatching a streaming AI service request.
 * Used by both authenticated (sendMessage) and loginless (sendLoginlessMessage) thunks.
 */
export async function dispatchStreamingRequest(
  dispatch: (action: unknown) => void,
  streamingFn: StreamingFn,
  id: string,
  messages: ChatbotMessages
): Promise<ChatbotResponse> {
  dispatch(setStreamingResponse(""));
  dispatch(setIsStreaming(true));

  try {
    const result = await streamingFn(id, messages, {
      onChunk: (chunk) => dispatch(appendStreamingResponse(chunk)),
      onComplete: () => dispatch(setIsStreaming(false)),
      onError: (error) => {
        dispatch(setIsStreaming(false));
        captureException(error instanceof Error ? error : new Error(String(error)));
      },
    });

    return {
      messages: result.messages,
      threadId: result.threadId,
    };
  } catch (error) {
    dispatch(setIsStreaming(false));
    throw error;
  }
}
